import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { EditUserFormValuesT } from '../../declarations';
import {
  CTAContainerStyled,
  FormWrapStyled,
  InputWrapStyled,
  SubmitButtonStyled,
  UserFormStyled,
  WarningStyled,
} from './UserEditForm.styled';
import validationSchema from './validationSchema';
import { FormInput } from 'components/Form';
import FormPhoneInput from 'components/Form/FormPhoneInput/FormPhoneInput';
import Typography from 'components/Typography/Typography';
import { useUserProfile } from 'contexts/UserProfileContext';

type UserEditFormPropsT = {
  onSubmit: SubmitHandler<EditUserFormValuesT>;
};

const UserEditForm: React.FC<UserEditFormPropsT> = ({ onSubmit }) => {
  /**
   * Hooks
   */
  const { userProfile, isUpdatingProfile, isSuccessfullyUpdatedProfile } = useUserProfile();
  const defaultValues = useMemo(
    () => ({
      lastname: userProfile?.lastname,
      firstname: userProfile?.firstname,
      phoneNumber: `+${userProfile?.phoneNumber}`,
    }),
    [userProfile]
  );
  const formMethods = useForm<EditUserFormValuesT>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues,
  });

  /**
   * Variables
   */
  const { handleSubmit, formState, reset } = formMethods;
  const { isDirty } = formState;
  const buttonShouldBeReset = !isDirty && isSuccessfullyUpdatedProfile;

  /**
   * Effects
   */
  useEffect(() => {
    if (isSuccessfullyUpdatedProfile) {
      reset(defaultValues, { keepDirty: false });
    }
  }, [isSuccessfullyUpdatedProfile, defaultValues, reset]);

  return (
    <UserFormStyled>
      <Typography variant="h4" color="darkBlue">
        Edit Your Profile
      </Typography>
      <FormWrapStyled>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputWrapStyled>
              <FormInput
                name="firstname"
                placeholder="First name"
                label="Enter your first name"
                isRequired
              />
            </InputWrapStyled>
            <InputWrapStyled>
              <FormInput
                name="lastname"
                placeholder="Last name"
                label="Enter your last name"
                isRequired
              />
            </InputWrapStyled>
            <InputWrapStyled>
              <FormPhoneInput
                name="phoneNumber"
                placeholder="+1000000000"
                label="Enter your mobile number"
                defaultCountry="US"
                isRequired
                international
                withCountryCallingCode
              />
            </InputWrapStyled>

            <InputWrapStyled>
              <FormInput
                name="photoFiles"
                type="file"
                accept="image/jpeg, image/png"
                label="Choose your photo (must be .png or .jpeg format)"
              />
            </InputWrapStyled>
            <Typography variant="body4" color="error">
              <i>* Indicates required fields</i>
            </Typography>
            <WarningStyled variant="body4">
              <i>If you want to change email, please connect with your WarrCloud Agent</i>
            </WarningStyled>
            <CTAContainerStyled justify="end">
              <SubmitButtonStyled
                type="submit"
                size="xl"
                variant="contained"
                isLoading={isUpdatingProfile}
                disabled={isUpdatingProfile || !isDirty}
                isSuccess={buttonShouldBeReset}
              >
                {buttonShouldBeReset ? 'Saved successfully' : 'Save changes'}
              </SubmitButtonStyled>
            </CTAContainerStyled>
          </form>
        </FormProvider>
      </FormWrapStyled>
    </UserFormStyled>
  );
};

export default UserEditForm;
